import React, { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import NumberPadDialog from "../NumberPadDialog";
import SelectDialog from "../SelectDialog";
import InterruptWorkDetailDialod from "./InterruptWorkDetailDialod";
import Button from "../forms/buttons/Button";
import GreenButton from "../forms/buttons/GreenButton";
import OutlineButton from "../forms/buttons/OutlineButton";
import { constantService } from "../../services/ConstantService";
import { strings } from "../../Strings";

/**
 * 作業内容詳細テーブルコンポーネント(新規登録)
 * @param {string} title 作業内容
 * @param {string} subtitle 作業内容
 * @param {object} workContents 作業内容
 * @param {Function} onChange 項目変更時の処理
 * @param {Function} onReset 内容リセット時の処理
 * @param {Function} onStart 作業開始時の処理
 * @param {object} errors エラーメッセージ
 * @class WorkContentCreateTable
 */
export default function WorkContentCreateTable({
  title,
  subtitle,
  workContents,
  onChange,
  onReset,
  onStart,
  errors,
}) {
  const [activeNumberPad, setActiveNumberPad] = useState(null);
  const [activeSelect, setActiveSelect] = useState(null);
  const [options, setOptions] = useState([]);
  const [otherOptions, setOtherOptions] = useState([]);
  const [workPlaces, setWorkPlaces] = useState([]);
  const [modelTypes, setModelTypes] = useState([]);
  const [modelNumbers, setModelNumbers] = useState([]);
  const [workProcesses, setWorkProcesses] = useState([]);
  const [selectedInterruptWork, setSelectedInterruptWork] = useState(null);

  /**
   * 作業職場一覧、機種一覧、機番一覧、工程一覧、停止ロス作業一覧を取得する
   * @memberof WorkContentTable
   */
  useEffect(() => {
    const getWorkPlaces = async () => {
      const result = await constantService.getWorkPlaces();
      setWorkPlaces(result);
    };
    const getModelTypes = async () => {
      const result = await constantService.getModelTypes();
      setModelTypes(result);
    };
    const getModelNumbers = async () => {
      const result = await constantService.getModelNumbers();
      setModelNumbers(result);
    };
    const getWorkProcesses = async () => {
      const result = await constantService.getWorkProcesses();
      setWorkProcesses(result);
    };
    getWorkPlaces();
    getModelTypes();
    getModelNumbers();
    getWorkProcesses();
  }, []);
  /**
   * オプションの値を設定する
   * @memberof WorkContentTable
   */
  useEffect(() => {
    switch (activeSelect) {
      case "workplaceId":
        setOptions(workPlaces);
        break;
      case "workProcessId":
        setOptions(workProcesses);
        setOtherOptions(workProcesses);
        break;
      case "modelNumberId":
        setOptions(modelNumbers);
        setOtherOptions(modelNumbers);
        break;
      case "modelTypeId":
        setOptions(modelTypes);
        break;
      default:
        setOptions([]);
        setOtherOptions([]);
        break;
    }
  }, [activeSelect, workPlaces, workProcesses, modelNumbers, modelTypes]);
  /**
   * 項目の値を変更し、ダイアログを閉じる
   * @param {string} name 項目名
   * @param {string} value 値
   * @memberof NoloadWorkContentTable
   */
  const onInputChange = (name, value) => {
    onChange(name, value);
    setActiveNumberPad(null);
    setActiveSelect(null);
    setOptions([]);
    setOtherOptions([]);
  };
  const dialogTitle =
    strings.WORK_CONTENT_TITLES.find(
      (o) => o.name === activeNumberPad || o.name === activeSelect,
    )?.title ?? "";
  const dialogSubTitle =
    strings.WORK_CONTENT_TITLES.find(
      (o) => o.name === activeNumberPad || o.name === activeSelect,
    )?.subtitle ?? "";

  const workPlace = workPlaces.find(
    (o) => o.value === workContents.workplaceId,
  )?.label;
  const modelType = modelTypes.find(
    (o) => o.value === workContents.modelTypeId,
  )?.label;
  const workProcess = workProcesses.find(
    (o) => o.value === workContents.workProcessId,
  )?.label;
  const modelNumber = modelNumbers.find(
    (o) => o.value === workContents.modelNumberId,
  )?.label;
  return (
    <>
      <section id="work-content-table" className={isMobile ? "mobile" : ""}>
        <div className="sheet-head">
          <div className="sheet-title">
            <span className="sheet-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>
              <h2 id="manualTitle">{title}</h2>
              <p>{subtitle}</p>
            </div>
          </div>
          <span className="status-badge">未入力</span>
        </div>

        <div className="manual-grid">
          <div className="label-cell">作業種別</div>
          <div className="value-cell">
            <span className="type-direct">直接作業</span>
          </div>

          <div className="label-cell">職場</div>
          <div className="value-cell">
            <div className="value-inner">
              <Button
                title="作業職場を選択"
                onClick={() => setActiveSelect("workplaceId")}
              />
              <span className={`selected-value ${!workPlace ? "empty" : ""}`}>
                {workPlace ?? "未選択"}
              </span>
            </div>
            {errors?.workplaceId && (
              <span className="error">{errors?.workplaceId}</span>
            )}
          </div>

          <div className="label-cell">機種</div>
          <div className="value-cell">
            <div className="value-inner">
              <Button
                title="機種を選択"
                onClick={() => setActiveSelect("modelTypeId")}
              />
              <span className={`selected-value ${!modelType ? "empty" : ""}`}>
                {modelType ?? "未選択"}
              </span>
              <p className="hint">
                検索用ソフトウェアキーボードで絞り込みできます
              </p>
            </div>
            {errors?.modelTypeId && (
              <span className="error">{errors?.modelTypeId}</span>
            )}
          </div>

          <div className="label-cell">工程</div>
          <div className="value-cell">
            <div className="value-inner">
              <Button
                title="工程を選択"
                onClick={() => setActiveSelect("workProcessId")}
              />
              <span className={`selected-value ${!workProcess ? "empty" : ""}`}>
                {workProcess ?? "未選択"}
              </span>
            </div>
            {errors?.workProcessId && (
              <span className="error">{errors?.workProcessId}</span>
            )}
          </div>

          <div className="label-cell">機番</div>
          <div className="value-cell">
            <div className="value-inner">
              <Button
                title="機番を選択"
                onClick={() => setActiveSelect("modelNumberId")}
              />
              <span className={`selected-value ${!modelNumber ? "empty" : ""}`}>
                {modelNumber ?? "未選択"}
              </span>
            </div>
            {errors?.modelNumberId && (
              <span className="error">{errors?.modelNumberId}</span>
            )}
          </div>

          <div className="label-cell">出来高</div>
          <div className="value-cell">
            <div className="value-inner">
              <Button
                title="開始時出来高を入力"
                onClick={() => setActiveNumberPad("startVolume")}
              />
              <span
                className={`yield-display ${!workContents.startVolume ? "empty" : ""}`}
              >
                {workContents.startVolume ?? "未入力"}
              </span>
            </div>
            {errors?.startVolume && (
              <span className="error">{errors?.startVolume}</span>
            )}
          </div>
        </div>

        <div className="bottom-actions">
          <OutlineButton title="入力をクリア" onClick={onReset} />
          <GreenButton title="作業を開始する" onClick={onStart} />
        </div>
      </section>

      <NumberPadDialog
        title={dialogTitle}
        subtitle={dialogSubTitle}
        name={activeNumberPad}
        value={workContents?.[activeNumberPad]}
        onChange={onInputChange}
        open={activeNumberPad !== null}
        onClose={() => setActiveNumberPad(null)}
      />
      <SelectDialog
        title={dialogTitle}
        subtitle={dialogSubTitle}
        name={activeSelect}
        value={workContents?.[activeSelect]}
        options={options}
        otherOptions={otherOptions}
        showKeypad={activeSelect === "modelTypeId"}
        onChange={onInputChange}
        open={activeSelect !== null}
        onClose={() => setActiveSelect(null)}
      />
      <InterruptWorkDetailDialod
        open={selectedInterruptWork != null}
        onClose={() => setSelectedInterruptWork(null)}
        interruptWorkId={selectedInterruptWork?.interruptWorkId}
        type={selectedInterruptWork?.type}
      />
    </>
  );
}
